import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Input, InputNumber, Radio, Select, Spin, Switch } from "antd";
import { EditOutlined, LoadingOutlined, SaveOutlined } from "@ant-design/icons";
import XButton from "../../../common/components/Button";
import { useAppDispatch } from "../../../../store/store";
import {
  CreatePetThunk,
  CreateDocPresignUrlForPetThunk,
  UpdatePetThunk,
} from "../../redux/thunk";
import { sleep } from "../../../../utils";
import { useSafeBackNavigation } from "../../../../shared/Route/useSafeBackNavigation";
import type {
  CreatePetRequest,
  PetProfileFormValues,
  PresignUrlRequest,
  UpdatePetRequest,
  UploadFileItem,
} from "../../ts";
import TextArea from "antd/es/input/TextArea";
import { PETS, MB_5 } from "../../../../constant";
import { v4 as uuidv4 } from "uuid";
import { Image } from "antd";
import { useApp } from "../../../../shared/AppProvider/AppProvider";
import { SkeletonPetProfileForm } from "./Skeletons/SkeletonPetProfileForm";
import { isEqual } from "lodash";

type PetProfileFormProps = {
  mode: "create" | "edit";
  initialValues?: Partial<PetProfileFormValues>;
};

export const PetProfileForm = (props: PetProfileFormProps) => {
  const { mode, initialValues } = props;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { navigate } = useSafeBackNavigation("/");
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<UploadFileItem[]>([]);
  const dispatch = useAppDispatch();
  const petRefId = useRef(uuidv4());
  const { notify } = useApp();
  const watchedValues = Form.useWatch([], form);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      petRefId.current = initialValues.id as string;
      if (initialValues.images) {
        setSelectedFiles(
          initialValues.images?.map(
            (file) =>
              ({
                id: file.id,
                status: "done",
                progress: 100,
                url: file.url,
                is_primary: file.is_primary,
                file_key: file.key,
              }) as UploadFileItem,
          ),
        );
      }
    }
  }, [initialValues]);

  const normalizeForm = (values: PetProfileFormValues) => ({
    name: values.name?.trim() || "",
    species: values.species,
    breed: values.breed?.trim() || "",
    gender: values.gender,
    age: values.age,
    size: values.size,

    vaccinated: !!values.vaccinated,
    medical_conditions: values.medical_conditions || "",
    allergies: values.allergies || "",
    medications: values.medications || "",
    vet_contact: values.vet_contact || "",

    feeding_schedule: values.feeding_schedule,
    food_notes: values.food_notes || "",
    walks_per_day: values.walks_per_day,
    walk_duration: values.walk_duration,

    friendly_dogs: !!values.friendly_dogs,
    friendly_cats: !!values.friendly_cats,
    friendly_children: !!values.friendly_children,

    energy_level: values.energy_level,
    behavior_notes: values.behavior_notes || "",
  });

  const normalizeFiles = (files: UploadFileItem[]) =>
    files
      .filter((f) => f.status !== "error")
      .map((f) => ({
        id: f.id || null,
        file_key: f.file_key || null,
        is_primary: !!f.is_primary,
      }))
      .sort((a, b) => {
        // stable order so comparison doesn't break due to array order
        return (a.file_key || "").localeCompare(b.file_key || "");
      });

  const isDirty = useMemo(() => {
    if (mode !== "edit" || !initialValues) return false;

    const normalizedInitial = {
      ...normalizeForm(initialValues as PetProfileFormValues),
      images: normalizeFiles(
        initialValues.images?.map(
          (file) =>
            ({
              id: file.id,
              status: "done",
              progress: 100,
              url: file.url,
              is_primary: file.is_primary,
              file_key: file.key,
            }) as UploadFileItem,
        ) || [],
      ),
    };

    const normalizedCurrent = {
      ...normalizeForm(watchedValues as PetProfileFormValues),
      images: normalizeFiles(selectedFiles),
    };

    return (
      !isEqual(normalizedInitial, normalizedCurrent) ||
      initialValues.images?.length !== selectedFiles.length
    );
  }, [watchedValues, selectedFiles, initialValues, mode]);

  const uploadFile = async (item: UploadFileItem, index: number) => {
    await sleep(300);
    const requestParams = {
      id: petRefId.current,
      file_type: item.file?.type,
      file_name: item.file?.name,
    } as PresignUrlRequest;

    const { data } = await dispatch(
      CreateDocPresignUrlForPetThunk(requestParams),
    ).unwrap();

    const formData = new FormData();
    Object.entries(data.fields).forEach(([k, v]) => {
      formData.append(k, v as string);
    });

    formData.append("file", item.file as File);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", data.upload_url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setSelectedFiles((prev) => {
          const copy = [...prev];
          copy[index] = {
            ...copy[index],
            progress: percent,
          };
          return copy;
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status === 204 || xhr.status === 201) {
        setSelectedFiles((prev) => {
          const copy = [...prev];
          copy[index] = {
            ...copy[index],
            status: "done",
            progress: 100,
            file_key: data.file_key,
          };
          return copy;
        });
      }
    };

    xhr.onerror = () => {
      setSelectedFiles((prev) => {
        const copy = [...prev];
        copy[index] = {
          ...copy[index],
          status: "error",
        };
        return copy;
      });
    };

    xhr.send(formData);
  };

  const handleFinish = async (values: PetProfileFormValues) => {
    try {
      setLoading(true);
      await sleep(300);

      const payload = {
        id: petRefId.current,

        // ================= BASIC INFO =================
        name: values.name,
        species: values.species,
        breed: values.breed || "",
        gender: values.gender,
        age: values.age,
        size: values.size,

        // ================= HEALTH =================
        vaccinated: values.vaccinated ?? false,
        medical_conditions: values.medical_conditions || "",
        allergies: values.allergies || "",
        medications: values.medications || "",
        vet_contact: values.vet_contact || "",

        // ================= ROUTINE =================
        feeding_schedule: values.feeding_schedule,
        food_notes: values.food_notes || "",
        walks_per_day: values.walks_per_day,
        walk_duration: values.walk_duration,

        // ================= BEHAVIOR =================
        friendly_dogs: values.friendly_dogs ?? false,
        friendly_cats: values.friendly_cats ?? false,
        friendly_children: values.friendly_children ?? false,

        energy_level: values.energy_level,
        behavior_notes: values.behavior_notes || "",

        // ================= FILES =================
        images: selectedFiles
          .filter((file) => file.status !== "error")
          .map((fileItem) => ({
            id: fileItem.id,
            key: fileItem.file_key || fileItem.file_key,
            name: fileItem.file?.name,
            size: fileItem.file?.size,
            type: fileItem.file?.type,
            is_primary: fileItem.is_primary,
          })),
      } as PetProfileFormValues;

      const result =
        mode === "create"
          ? await dispatch(CreatePetThunk(payload as CreatePetRequest)).unwrap()
          : await dispatch(
              UpdatePetThunk(payload as UpdatePetRequest),
            ).unwrap();

      form.resetFields();
      setSelectedFiles([]);

      // reset pet ref id for next creation
      petRefId.current = uuidv4();

      notify("success", result.message);

      navigate("/profile/my-pets");
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error(error.message);
        notify("error", error?.message || "Something went wrong");
      } else {
        notify("error", "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files) return;

    const fileArray = Array.from(files)
      .filter((file) => file.size <= MB_5)
      .map(
        (file) =>
          ({
            file,
            status: "uploading",
            progress: 0,
            type: "new",
            is_primary: false,
          }) as UploadFileItem,
      );

    fileArray.forEach((file, i) => uploadFile(file, selectedFiles.length + i));
    const newFiles = [...selectedFiles, ...fileArray];
    setSelectedFiles(newFiles);
    form.setFieldsValue({
      images: newFiles,
    });
  };

  if (mode == "edit" && !initialValues) return <SkeletonPetProfileForm />;

  return (
    <Spin spinning={loading} indicator={<LoadingOutlined spin />} size="large">
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        requiredMark="optional"
        onFinish={handleFinish}
      >
        <div className="grid grid-cols-1 gap-y-2">
          {/* ================= BASIC INFO ================= */}
          <h2 className="text-lg font-semibold mb-6 dark:text-white">
            1. Basic Information
          </h2>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Pet Name
              </label>
            }
            name="name"
            rules={[{ required: true, message: "Pet name is required" }]}
          >
            <Input
              placeholder="e.g. Buddy"
              size="large"
              className={
                mode === "create"
                  ? `!bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white`
                  : ""
              }
              disabled={mode === "edit"}
            />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Species
              </label>
            }
            name="species"
            rules={[{ required: true }]}
          >
            <Select
              size="large"
              placeholder="Select species"
              disabled={mode === "edit"}
              className={
                mode === "create"
                  ? ` w-full [&_.ant-select-selector]:!bg-white dark:[&_.ant-select-selector]:!bg-white/5 [&_.ant-select-selector]:!text-gray-900 dark:[&_.ant-select-selector]:!text-white
    [&_.ant-select-selection-placeholder]:!text-gray-400 dark:[&_.ant-select-selection-placeholder]:!text-gray-500 [&_.ant-select-selection-item-remove]:!text-gray-500 dark:[&_.ant-select-selection-item-remove]:!text-white`
                  : ""
              }
              dropdownClassName={
                mode === "create"
                  ? `!bg-white dark:!bg-gray-700 [&_.ant-select-item]:!text-gray-900 dark:[&_.ant-select-item]:!text-gray-200 dark:[&_.ant-select-item-option-selected]:!bg-white/10 dark:[&_.ant-select-item-option-active]:!bg-white/5`
                  : ""
              }
            >
              {PETS.map((pet) => (
                <Select.Option key={pet.value} value={pet.value}>
                  {pet.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Breed
              </label>
            }
            name="breed"
          >
            <Input
              size="large"
              placeholder="Optional"
              className="!bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
            />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Gender
              </label>
            }
            name="gender"
            rules={[{ required: true }]}
          >
            <Radio.Group size="large">
              <Radio value="M">Male</Radio>
              <Radio value="F">Female</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Age (years)
              </label>
            }
            name="age"
            rules={[
              { required: true },
              { type: "number", min: 0, max: 40, message: "Enter valid age" },
            ]}
          >
            <InputNumber
              size="large"
              className="w-full !bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
            />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Size
              </label>
            }
            name="size"
            rules={[{ required: true }]}
          >
            <Select
              size="large"
              placeholder="Select size"
              className=" w-full [&_.ant-select-selector]:!bg-white dark:[&_.ant-select-selector]:!bg-white/5
    [&_.ant-select-selector]:!text-gray-900
    dark:[&_.ant-select-selector]:!text-white
    [&_.ant-select-selection-placeholder]:!text-gray-400
    dark:[&_.ant-select-selection-placeholder]:!text-gray-500
    [&_.ant-select-selection-item-remove]:!text-gray-500
    dark:[&_.ant-select-selection-item-remove]:!text-white
  "
              dropdownClassName="
    !bg-white
    dark:!bg-gray-700
    [&_.ant-select-item]:!text-gray-900
    dark:[&_.ant-select-item]:!text-gray-200
    dark:[&_.ant-select-item-option-selected]:!bg-white/10
    dark:[&_.ant-select-item-option-active]:!bg-white/5
  "
            >
              <Select.Option value="small">Small</Select.Option>
              <Select.Option value="medium">Medium</Select.Option>
              <Select.Option value="large">Large</Select.Option>
            </Select>
          </Form.Item>

          {/* ================= HEALTH ================= */}
          <h2 className="text-lg font-semibold my-6 dark:text-white">
            2. Health
          </h2>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Vaccinated
              </label>
            }
            name="vaccinated"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Medical Conditions
              </label>
            }
            name="medical_conditions"
          >
            <TextArea
              size="large"
              rows={3}
              className="!bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
              showCount
              count={{ max: 500 }}
            />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Allergies
              </label>
            }
            name="allergies"
          >
            <TextArea
              size="large"
              rows={3}
              className="!bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
              showCount
              count={{ max: 300 }}
            />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Medications
              </label>
            }
            name="medications"
          >
            <TextArea
              size="large"
              rows={3}
              className="!bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
              showCount
              count={{ max: 300 }}
            />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Vet Contact
              </label>
            }
            name="vet_contact"
            rules={[{ required: true, message: "Vet contact is required" }]}
          >
            <Input
              size="large"
              placeholder="Clinic name / phone"
              className="!bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
            />
          </Form.Item>

          {/* ================= ROUTINE ================= */}
          <h2 className="text-lg font-semibold my-6 dark:text-white">
            3. Routine
          </h2>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Feeding Schedule
              </label>
            }
            name="feeding_schedule"
            rules={[{ required: true }]}
          >
            <Select
              size="large"
              className="
    w-full
    [&_.ant-select-selector]:!bg-white
    dark:[&_.ant-select-selector]:!bg-white/5
    [&_.ant-select-selector]:!text-gray-900
    dark:[&_.ant-select-selector]:!text-white
    [&_.ant-select-selection-placeholder]:!text-gray-400
    dark:[&_.ant-select-selection-placeholder]:!text-gray-500
    [&_.ant-select-selection-item-remove]:!text-gray-500
    dark:[&_.ant-select-selection-item-remove]:!text-white
  "
              dropdownClassName="
    !bg-white
    dark:!bg-gray-700
    [&_.ant-select-item]:!text-gray-900
    dark:[&_.ant-select-item]:!text-gray-200
    dark:[&_.ant-select-item-option-selected]:!bg-white/10
    dark:[&_.ant-select-item-option-active]:!bg-white/5
  "
            >
              <Select.Option value="once">Once a day</Select.Option>
              <Select.Option value="twice">Twice a day</Select.Option>
              <Select.Option value="custom">Custom</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const isCustom = getFieldValue("feeding_schedule") === "custom";

              return (
                <Form.Item
                  name="food_notes"
                  label={
                    <label className="font-medium text-gray-900 dark:text-gray-200">
                      Food Notes
                    </label>
                  }
                  rules={[
                    {
                      required: isCustom,
                      message: "Food notes required for custom schedule",
                    },
                  ]}
                >
                  <TextArea
                    rows={3}
                    className="!bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
                    showCount
                    count={{ max: 500 }}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Walks per Day
              </label>
            }
            name="walks_per_day"
            rules={[{ required: true }, { type: "number", min: 0, max: 10 }]}
          >
            <InputNumber
              size="large"
              className="w-full !bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
            />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Walk Duration
              </label>
            }
            name="walk_duration"
            rules={[{ required: true }]}
          >
            <Select
              size="large"
              className="
    w-full
    [&_.ant-select-selector]:!bg-white
    dark:[&_.ant-select-selector]:!bg-white/5
    [&_.ant-select-selector]:!text-gray-900
    dark:[&_.ant-select-selector]:!text-white
    [&_.ant-select-selection-placeholder]:!text-gray-400
    dark:[&_.ant-select-selection-placeholder]:!text-gray-500
    [&_.ant-select-selection-item-remove]:!text-gray-500
    dark:[&_.ant-select-selection-item-remove]:!text-white
  "
              dropdownClassName="
    !bg-white
    dark:!bg-gray-700
    [&_.ant-select-item]:!text-gray-900
    dark:[&_.ant-select-item]:!text-gray-200
    dark:[&_.ant-select-item-option-selected]:!bg-white/10
    dark:[&_.ant-select-item-option-active]:!bg-white/5
  "
            >
              <Select.Option value={15}>15 mins</Select.Option>
              <Select.Option value={30}>30 mins</Select.Option>
              <Select.Option value={60}>60 mins</Select.Option>
            </Select>
          </Form.Item>

          {/* ================= BEHAVIOR ================= */}
          <h2 className="text-lg font-semibold my-6 dark:text-white">
            4. Behavior
          </h2>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Friendly with Dogs
              </label>
            }
            name="friendly_dogs"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Friendly with Cats
              </label>
            }
            name="friendly_cats"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Friendly with Children
              </label>
            }
            name="friendly_children"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Energy Level
              </label>
            }
            name="energy_level"
            rules={[{ required: true, message: "Please select energy level" }]}
          >
            <Select
              size="large"
              className="
    w-full
    [&_.ant-select-selector]:!bg-white
    dark:[&_.ant-select-selector]:!bg-white/5
    [&_.ant-select-selector]:!text-gray-900
    dark:[&_.ant-select-selector]:!text-white
    [&_.ant-select-selection-placeholder]:!text-gray-400
    dark:[&_.ant-select-selection-placeholder]:!text-gray-500
    [&_.ant-select-selection-item-remove]:!text-gray-500
    dark:[&_.ant-select-selection-item-remove]:!text-white
  "
              dropdownClassName="
    !bg-white
    dark:!bg-gray-700
    [&_.ant-select-item]:!text-gray-900
    dark:[&_.ant-select-item]:!text-gray-200
    dark:[&_.ant-select-item-option-selected]:!bg-white/10
    dark:[&_.ant-select-item-option-active]:!bg-white/5
  "
            >
              <Select.Option value="low">Low</Select.Option>
              <Select.Option value="medium">Medium</Select.Option>
              <Select.Option value="high">High</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <label className="font-medium text-gray-900 dark:text-gray-200">
                Special Notes
              </label>
            }
            name="behavior_notes"
          >
            <TextArea
              size="large"
              rows={3}
              className="!bg-white dark:!bg-white/5 !text-gray-900 dark:!text-white"
              showCount
              count={{ max: 800 }}
            />
          </Form.Item>

          {/* ================= PHOTOS ================= */}
          <h2 className="text-lg font-semibold my-6 dark:text-white">
            5. Photos
          </h2>

          <Form.Item
            getValueFromEvent={(e) => {
              const files = e?.target?.files;
              if (!files) return [];

              const fileArray = Array.from(files as File[])
                .filter((file: File) => file.size <= MB_5)
                .map(
                  (file) =>
                    ({
                      file,
                      status: "uploading",
                      progress: 0,
                    }) as UploadFileItem,
                );

              fileArray.forEach((file, i) => uploadFile(file, i));
              const newFiles = [...selectedFiles, ...fileArray];
              setSelectedFiles(newFiles);
              return newFiles;
            }}
            name="images"
            valuePropName="images"
            rules={[
              { required: true, message: "At least one photo is required" },
              {
                validator: (_, value: UploadFileItem[] = []) => {
                  if (value.length === 0) return Promise.resolve(); // allow empty

                  const allowedTypes = [
                    "image/svg+xml",
                    "image/png",
                    "image/gif",
                    "image/jpeg",
                  ];

                  for (const uploadFile of value) {
                    const isNewFile = uploadFile.file instanceof File;
                    if (!isNewFile) continue;
                    if (
                      !allowedTypes.includes(uploadFile.file?.type as string)
                    ) {
                      return Promise.reject(
                        new Error(
                          `Invalid file type: ${uploadFile.file?.name as string}`,
                        ),
                      );
                    }
                    if (uploadFile.file && uploadFile.file.size > MB_5) {
                      return Promise.reject(
                        new Error(
                          `File too large: ${uploadFile.file.name} (max 5MB)`,
                        ),
                      );
                    }
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <input
              type="file"
              multiple
              hidden
              ref={inputFile}
              accept="image/svg+xml,image/png,image/gif,image/jpeg"
              onChange={handleFileChange}
            />
            <div className="flex gap-2 items-center flex-wrap">
              {selectedFiles.map((selectedFile, index) => {
                const is_primary = selectedFile.is_primary;

                if (selectedFile.status === "uploading") {
                  return (
                    <label className="w-32 h-32 border-1 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition">
                      <Spin indicator={<LoadingOutlined spin />} />
                    </label>
                  );
                }
                return (
                  <div
                    key={index}
                    className="relative group w-32 h-32 rounded-xl overflow-hidden border border-gray-300"
                  >
                    <Image
                      src={
                        selectedFile.url ||
                        URL.createObjectURL(selectedFile?.file as File)
                      }
                      alt={selectedFile.file?.name}
                      className="object-cover !w-32 !h-32 border border-gray-300 rounded-xl"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();

                        const updatedFiles = selectedFiles.filter(
                          (_, i) => i !== index,
                        );

                        if (
                          selectedFile.is_primary &&
                          updatedFiles.length > 0 &&
                          !updatedFiles.some((f) => f.is_primary)
                        ) {
                          updatedFiles[0].is_primary = true;
                        }

                        setSelectedFiles(updatedFiles);

                        form.setFieldValue("images", updatedFiles);
                      }}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    >
                      ×
                    </button>
                    {is_primary ? (
                      <div className="absolute bottom-1 left-1 bg-blue-600 text-white text-[10px] px-2 py-0.5 rounded-full">
                        Primary
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();

                          const updatedFiles = selectedFiles.map((f, i) => ({
                            ...f,
                            is_primary: i === index,
                          }));

                          setSelectedFiles(updatedFiles);

                          form.setFieldValue("images", updatedFiles);
                        }}
                        className="absolute bottom-1 left-1 bg-white/90 text-gray-700 text-[10px] px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition"
                      >
                        Set Primary
                      </button>
                    )}
                  </div>
                );
              })}

              {selectedFiles.length > 0 && (
                <label
                  onClick={() => inputFile.current?.click()}
                  className="w-32 h-32 border-1 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition"
                >
                  <div className="text-gray-800 leading-none dark:text-white">
                    +
                  </div>
                  <p className="mt-4  font-medium text-gray-800  dark:text-white">
                    Upload
                  </p>
                </label>
              )}

              {selectedFiles.length === 0 && (
                <div className="flex justify-center items-center rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-6 py-10 w-full">
                  <div className="text-center">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-500"
                    >
                      <path
                        d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z"
                        clipRule="evenodd"
                        fillRule="evenodd"
                      />
                    </svg>

                    <div className="mt-4 flex justify-center text-sm text-gray-600 dark:text-gray-400 gap-1">
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer font-semibold text-[#1677ff] hover:text-[#69b1ff] "
                      >
                        <span onClick={() => inputFile.current?.click()}>
                          Upload a file
                        </span>
                      </label>
                      <span>or drag and drop</span>
                    </div>

                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG, GIF up to 5MB each
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* </div> */}
          </Form.Item>
          <div className="flex justify-end mt-4 gap-2">
            <XButton
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              disabled={mode === "edit" ? !isDirty : false}
              iconPosition="start"
              icon={ mode === "edit" ? <EditOutlined /> : <SaveOutlined /> }
            >
              {mode === "edit" ? "Update Pet Profile" : "Create Pet Profile"}
            </XButton>
            <XButton
              type="default"
              size="large"
              loading={loading}
              onClick={() => navigate("/profile/my-pets")}
              // icon={<CloseOutlined />}
            >
              Cancel
            </XButton>
          </div>
        </div>
      </Form>
    </Spin>
  );
};
